/**
 * Employees, Contractors & Consultants — 12 documents.
 *
 * The single largest risk in this category is not any one clause: it is whether the person you are
 * paying is an employee or an independent contractor. That question is decided by how the working
 * relationship actually operates, the tests used to decide it differ by jurisdiction and by the
 * authority asking, and the label written on the contract does not settle it. Employment terms,
 * compensation timing and restrictive covenants are all jurisdiction-specific — this category routes
 * to counsel more often than any other, and deliberately ships no employment templates.
 */
export const PEOPLE_DOCS = [
  {
    id: "employment-agreement",
    name: "Employment Agreement",
    category: "people",
    subcategory: "Employment",
    aliases: [
      "Employment Contract",
      "Executive Employment Agreement",
      "Employment Terms Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Employee",
    whenYouNeedIt:
      "When you hire someone onto payroll with terms that go beyond a simple offer letter: an executive, a head of sales, a plant or operations lead, or anyone whose compensation includes equity, commission or a severance promise.",
    whatItDoes:
      "Sets out the role, the pay, the equity, what happens on termination and what the employee owes the company during and after employment — confidentiality, ownership of what they create, and any restrictions on where they go next.",
    whyItMatters:
      "In a small CPG company the first ten employees hold everything that is hard to replace: the formula, the co-packer relationship, the retailer contacts, the supplier pricing and the social accounts. An employment agreement is where you decide, in advance, whether those things stay with the company when the person leaves. It is also where the most jurisdiction-specific terms in your whole document set live — pay timing, notice, termination, severance and any restriction on future employment are governed by rules that differ sharply from place to place and change often, which is why this document belongs with counsel in the jurisdiction where the person actually works.",
    majorTerms: [
      "Role, reporting line and duties",
      "Base salary, pay frequency and review cadence",
      "Bonus or commission structure and how it is earned",
      "Equity grant, the plan it is issued under, and vesting",
      "Start date and any probationary or introductory period",
      "Termination rights and any notice or severance",
      "Confidentiality obligations",
      "Assignment of inventions and work product",
      "Restrictive covenants, if any, and their scope",
      "Expense policy and travel expectations",
      "Return of company property, samples and inventory",
      "Governing law and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "Equity promise without the underlying plan documents",
        why: "An agreement promising a percentage of the company with no plan, no grant and no board approval behind it creates an expectation you will have to buy out later, usually during diligence.",
      },
      {
        clause: "Vesting and what happens on departure",
        why: "Without vesting and a clear treatment of unvested equity on exit, someone who leaves in month five can keep a stake that makes the company harder to fund.",
      },
      {
        clause: "Severance triggers",
        why: "Severance promised for a vaguely defined 'termination without cause' with no definition of cause is a cash obligation you cannot forecast and cannot easily avoid.",
      },
      {
        clause: "Invention and work-product assignment",
        why: "If assignment is not written and signed, recipes, artwork, formulations and process improvements created by an employee may not sit cleanly with the company.",
      },
      {
        clause: "Restrictive covenants",
        why: "How far a restriction on competing or soliciting can go varies significantly by jurisdiction and is a counsel question, and an overreaching clause can be worth less than a narrower one that fits.",
      },
      {
        clause: "Commission treatment after termination",
        why: "Silence on orders written before departure but shipped or paid after it produces the most common post-employment dispute in a sales-led CPG business.",
      },
      {
        clause: "Governing law and place of work",
        why: "The employee's actual work location usually matters more than the company's home state, and picking the wrong one can leave the agreement out of step with the rules that apply.",
      },
    ],
    cpgIssues: [
      "Formula, process and specification knowledge walks out with an operations or product hire; confidentiality and invention assignment need to cover recipe iterations, run parameters and supplier formulations, not just documents.",
      "Retailer and distributor contacts are the most portable asset in CPG — decide in advance what a departing sales hire may take, and route any restriction on soliciting accounts to counsel in their jurisdiction.",
      "Supplier and co-packer pricing is competitively sensitive and travels easily to a competing brand; name it explicitly as confidential information.",
      "Brand social accounts, ambassador relationships and content libraries are routinely created under an employee's personal login; require company ownership and handover of credentials in writing.",
      "Employees who appear in brand content — founder-adjacent staff, demo leads, athletes on payroll — need a likeness release that survives their employment, or your ad library expires when they do.",
      "Plant, warehouse and demo roles come with safety, allergen and sanitation obligations that belong in the agreement or an incorporated policy, because a lapse is a product problem and not only a personnel one.",
    ],
    founderMistakes: [
      "Hiring a 'first employee' who was really a co-founder — promising a large equity stake in an email, with no vesting and no plan document, and paying to unwind it during a raise.",
      "Writing a bonus or commission plan into the agreement with no definition of when it is earned, then arguing about a departing salesperson's pipeline for months.",
      "Never signing invention assignment with the operations hire who reformulated the product, and discovering the gap during acquisition diligence.",
      "Copying an employment agreement from another state or country without checking whether its termination and pay terms fit where the employee actually works.",
      "Promising severance informally during recruiting and then having it treated as part of the deal when the relationship ended.",
      "Letting a sales lead run the brand's retailer relationships entirely through a personal phone and email, with no obligation to hand them over.",
    ],
    negotiationPoints: [
      "Issue equity only through an adopted plan with a board-approved grant, and make the agreement reference the grant rather than promise a percentage.",
      "Define cause, define good reason, and state exactly what severance is payable and on what conditions.",
      "State when commission and bonus are earned, and what happens to orders in flight at termination.",
      "Include a full assignment of inventions and works, with a specific carve-in for recipes, formulations, artwork and process improvements.",
      "Require handover of all accounts, credentials, contacts, samples and inventory as a condition of final pay processing, subject to local rules on final pay.",
      "Have counsel in the employee's work location review termination, pay timing and any restrictive covenant before the offer goes out.",
    ],
    redFlags: [
      "A promised equity percentage with no plan, no grant and no vesting.",
      "Severance triggered by an undefined term.",
      "No invention assignment or confidentiality obligation at all.",
      "A restrictive covenant lifted from a template written for a different jurisdiction.",
      "Commission terms that do not say when commission is earned.",
      "A guaranteed multi-year term with no termination rights on either side.",
      "Governing law chosen with no relationship to where the person actually works.",
    ],
    beforeYouSign: [
      "Board approval and plan documents for any equity being promised.",
      "A written compensation plan showing exactly how variable pay is calculated and earned.",
      "Counsel review in the jurisdiction where the employee will work, covering termination, pay and any restrictions.",
      "A list of the accounts, credentials, samples and equipment the role will hold, and the handover terms for each.",
    ],
    related: [
      "offer-letter",
      "employee-confidentiality-agreement",
      "employee-ip-assignment-agreement",
      "sales-employee-compensation-agreement",
      "separation-agreement",
      "equity-incentive-plan",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Termination, pay timing, severance and any restriction on future employment are governed by rules that differ by jurisdiction and change often, so a template drafted elsewhere can create exposure rather than protection.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "offer-letter",
    name: "Offer Letter",
    category: "people",
    subcategory: "Employment",
    aliases: ["Employment Offer Letter", "Letter of Offer", "Job Offer Letter"],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Candidate",
    whenYouNeedIt:
      "For every hire, before a start date is agreed. It is the document most small CPG companies actually use in place of a full employment agreement, which is exactly why it needs to be right.",
    whatItDoes:
      "Sets out the offer in writing: title, start date, pay, variable compensation, any equity, the conditions attached to the offer, and the other agreements the candidate will be asked to sign on day one.",
    whyItMatters:
      "Founders treat the offer letter as a friendly summary and then discover it is the whole contract, because nothing else ever gets signed. Whatever it says about equity, bonus, commission or job security is the deal. The most expensive version of this is an offer letter that mentions a percentage of the company without a plan behind it, or a commission scheme with no rules for when commission is earned — both create commitments you cannot see the size of until someone leaves. What an offer letter can and cannot say about the terms of employment also varies by jurisdiction, which is why it should be reviewed before it becomes your standard.",
    majorTerms: [
      "Title, reporting line and work location",
      "Start date and any conditions the offer depends on",
      "Base compensation and pay frequency",
      "Bonus or commission and a reference to the written plan",
      "Equity: number of units, plan name, vesting and board approval",
      "Benefits summary and effective dates",
      "The agreements the candidate must sign on or before day one",
      "Expense and travel expectations",
      "Statement of the employment relationship's terms and how it can end",
    ],
    clausesToWatch: [
      {
        clause: "Equity described as a percentage",
        why: "A percentage promised in a letter, with no share count, plan or grant, is read by the recipient as fixed forever and becomes a dispute at the first financing.",
      },
      {
        clause: "Compensation stated annually",
        why: "An annual salary figure in a letter can be read as a commitment for a full year, which is not usually what the company intended and is a point worth putting to counsel.",
      },
      {
        clause: "Bonus described without a plan",
        why: "A bonus mentioned with no written plan, no metrics and no discretion language becomes an expectation you will be arguing about at the end of the year.",
      },
      {
        clause: "Commission mentioned but not defined",
        why: "A commission rate with no rule for when it is earned, and no treatment of orders shipping after departure, is the single most common post-employment claim in CPG sales roles.",
      },
      {
        clause: "Conditions of the offer",
        why: "If the offer is not conditioned on signing the confidentiality and invention assignment agreements, you can end up with a started employee who has signed neither.",
      },
      {
        clause: "Terms describing how employment can end",
        why: "Language about job security or termination interacts with local rules and with anything a recruiter said, and getting the wording wrong here is difficult to correct later.",
      },
    ],
    cpgIssues: [
      "Sales hires arrive expecting a commission structure; if the letter names a rate without attaching the plan, you have committed to the rate and none of the rules around it.",
      "Demo, merchandising and field-marketing roles are often offered with variable hours and per-event pay, and how that is described in the letter should match how the role will actually be scheduled and paid.",
      "Operations and quality hires may need certification, training or background conditions satisfied before day one; make those express conditions of the offer.",
      "Early CPG hires frequently negotiate product allowances, samples or brand ambassador arrangements — put anything of value in the letter, or it becomes an informal entitlement.",
      "A candidate coming from a competing brand may carry obligations to their former employer; the letter should require them to confirm they are free to accept and not to bring confidential material with them.",
    ],
    founderMistakes: [
      "Offering '2% of the company' in an offer letter with no plan, no grant and no vesting, and paying to renegotiate it during the first priced round.",
      "Naming a commission rate in the letter and never producing the plan, so every rule was decided retroactively and in the employee's favour.",
      "Sending offer letters copied from a friend's company in another jurisdiction and inheriting terms that did not fit where the hire actually worked.",
      "Letting a hire start before signing confidentiality and invention assignment, then trying to get signatures after a disagreement had already begun.",
      "Describing a role as flexible and contractor-like in the letter while scheduling and supervising it exactly like an employee role.",
    ],
    negotiationPoints: [
      "Describe equity as a number of units under a named plan, subject to board approval and the plan's vesting terms.",
      "Attach the bonus or commission plan to the letter, or state that no variable compensation is payable until a written plan is signed.",
      "Make the offer expressly conditional on signing the confidentiality, invention assignment and any other day-one agreements.",
      "State pay as a rate per pay period as well as annually, if counsel in the relevant jurisdiction advises it.",
      "Ask the candidate to confirm in writing that accepting does not breach any obligation to a former employer.",
      "Have one standard letter reviewed by counsel and use it for every hire rather than drafting each one fresh.",
    ],
    redFlags: [
      "Equity expressed as a percentage with no plan or grant behind it.",
      "A bonus or commission promise with no written plan.",
      "No condition requiring confidentiality and invention assignment to be signed.",
      "Language promising job security or a fixed term that the company did not intend.",
      "A letter reused across jurisdictions without review.",
      "Terms agreed verbally in recruiting that are not reflected in the letter at all.",
    ],
    beforeYouSign: [
      "The written bonus or commission plan the letter refers to.",
      "Board approval and available pool for any equity being offered.",
      "Counsel review of your standard letter for the jurisdictions you hire in.",
    ],
    related: [
      "employment-agreement",
      "employee-confidentiality-agreement",
      "employee-ip-assignment-agreement",
      "sales-employee-compensation-agreement",
      "equity-incentive-plan",
      "stock-option-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "For most small CPG companies the offer letter is the entire employment contract, and what it may say about pay, equity and how employment ends is jurisdiction-specific.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "independent-contractor-agreement",
    name: "Independent Contractor Agreement",
    category: "people",
    subcategory: "Contractors",
    aliases: [
      "Contractor Agreement",
      "Freelance Agreement",
      "Services Agreement (Contractor)",
      "Freelance Services Agreement",
    ],
    stages: ["pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Independent Contractor",
    whenYouNeedIt:
      "Before anyone who is not on payroll starts work: a designer, a demo lead, a field marketer, a bookkeeper, a warehouse hand, a fractional operator. If they have already started, you need it now rather than at the next invoice.",
    whatItDoes:
      "Defines a services relationship: the scope of work, the fee, who owns the output, what is confidential, and that the person is running their own business rather than working as your employee.",
    whyItMatters:
      "Two things go wrong here, and both are expensive. The first is ownership: a contractor who designs your packaging, shoots your product photography or writes your label copy generally keeps the rights to it unless there is a signed assignment, so you can end up unable to use, modify or license your own artwork. The second is classification. Whether someone is an employee or an independent contractor is determined by how the relationship actually works, not by what the contract calls it, the tests differ by jurisdiction and by the authority applying them, and getting it wrong creates exposure that reaches back over the whole engagement. That question belongs with counsel before the pattern is set, not after someone complains.",
    majorTerms: [
      "Scope of services and deliverables",
      "Fee structure: fixed, hourly, per project or per event",
      "Invoicing and payment terms",
      "Term and termination on notice",
      "Ownership and assignment of work product",
      "Confidentiality obligations",
      "Independent contractor status and control over how work is performed",
      "Contractor's own tools, equipment and expenses",
      "Ability to work for others and any conflict limits",
      "Insurance and indemnity for on-site or event work",
      "Use of subcontractors and the contractor's responsibility for them",
    ],
    clausesToWatch: [
      {
        clause: "Ownership and assignment of work product",
        why: "Payment alone does not usually transfer rights in creative work, so without a present assignment your designer, photographer or copywriter keeps what you paid for.",
      },
      {
        clause: "Status and control language",
        why: "A clause calling someone a contractor does not settle classification if the working pattern says otherwise, so this is where the agreement and the reality have to match.",
      },
      {
        clause: "Exclusivity and set schedules",
        why: "Requiring set hours, exclusivity and daily supervision makes the engagement look very different from what the contract says it is, which is exactly the tension to raise with counsel.",
      },
      {
        clause: "Termination and notice",
        why: "An engagement with no termination right, or one that requires payment of the full project fee on cancellation, removes your ability to stop work that is not going well.",
      },
      {
        clause: "Third-party materials in deliverables",
        why: "Stock images, fonts and music licensed to the contractor rather than to you can leave your packaging or your ad unusable at scale.",
      },
      {
        clause: "Insurance for on-site and event work",
        why: "Demo staff and warehouse contractors work in other people's buildings, and retailers and 3PLs usually require coverage that the contractor may not carry.",
      },
    ],
    cpgIssues: [
      "Brand ambassadors and demo staff are the classic pressure point: they are engaged as contractors but scheduled by you, given shift times, dressed in your brand and told exactly how to run the table — a pattern worth putting to counsel before you scale it across a market.",
      "Packaging designers, label artists and product photographers create the assets your brand is built from; without a written assignment the artwork on your can may not be yours to modify, license or transfer in a sale.",
      "Field marketing and sampling contractors handle food and beverage in public, so allergen handling, sampling hygiene and event insurance obligations belong in the agreement.",
      "Fractional operators — a part-time supply chain lead, a fractional CMO — often hold co-packer, supplier and retailer relationships; confidentiality and non-use of your supplier pricing matter as much as the deliverable.",
      "Contractors who develop or tweak a formulation need a specific assignment covering formulations, specifications and process know-how, not a generic clause about 'documents'.",
      "Retailers and 3PLs frequently require proof of insurance before a contractor may work on site, and an uninsured demo contractor can cost you the event and the account relationship.",
    ],
    founderMistakes: [
      "Paying a designer for the packaging, never getting an assignment, and being unable to hand over usable artwork files during an acquisition.",
      "Engaging demo and ambassador staff as contractors while scheduling them like employees across dozens of shifts, and never once asking counsel whether the pattern held up.",
      "Using a one-page scope of work with no IP, confidentiality or termination terms because the contractor was a friend.",
      "Discovering that the stock photography in the brand's launch campaign was licensed to the agency's contractor personally, and could not be used in paid ads.",
      "Sending a contractor to run demos in a chain account with no insurance, and being removed from the program after an incident.",
      "Treating a long-running full-time contractor as a permanent arrangement for years without ever revisiting the classification question.",
    ],
    negotiationPoints: [
      "Include a present assignment of all work product plus an obligation to deliver source files and to assist with any further paperwork needed.",
      "Require disclosure and licensing of any third-party materials, with rights broad enough for packaging, paid advertising and resale.",
      "Keep control over the result rather than the method, and avoid imposing fixed schedules and exclusivity unless counsel has confirmed the arrangement.",
      "Add confidentiality that names formulations, supplier pricing, co-packer terms and customer lists specifically.",
      "Require insurance for any on-site, warehouse or event work, with certificates before the first shift.",
      "Keep terms short with termination on notice, and pay for work completed rather than the full project fee on cancellation.",
      "Ask counsel to review the working pattern, not just the document, for any role you plan to run at scale.",
    ],
    redFlags: [
      "No assignment of work product, or an assignment conditioned on final payment with no interim licence.",
      "A contractor engagement that sets fixed hours, requires exclusivity and supervises daily work.",
      "No confidentiality clause where the contractor will see formulations, pricing or customer lists.",
      "Deliverables built on third-party materials with no licence passed through to you.",
      "No termination right, or a full-fee cancellation charge.",
      "On-site or event work with no insurance requirement.",
    ],
    beforeYouSign: [
      "A written scope with deliverables, milestones and what 'done' means.",
      "Confirmation of what third-party materials will be used and on what licence.",
      "Certificates of insurance for any on-site, warehouse or event work.",
      "Counsel input on classification for any role you will engage repeatedly or at scale.",
    ],
    related: [
      "contractor-work-for-hire-agreement",
      "consultant-agreement",
      "contractor-nda",
      "contractor-termination-agreement",
      "demo-contractor-agreement",
      "work-for-hire-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Whether a worker is an employee or a contractor turns on how the relationship actually operates and is tested differently by jurisdiction and by authority, so the label in the document does not resolve it.",
    templateId: "independent-contractor-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "consultant-agreement",
    name: "Consultant Agreement",
    category: "people",
    subcategory: "Contractors",
    aliases: [
      "Consulting Agreement",
      "Professional Services Agreement",
      "Fractional Executive Agreement",
      "Retainer Agreement",
    ],
    stages: ["development", "pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Consultant",
    whenYouNeedIt:
      "When you bring in expertise you do not have in-house — a food scientist, a supply chain operator, a retail sales veteran, a fractional finance lead — usually on a retainer or a project fee rather than a payroll.",
    whatItDoes:
      "Engages an expert for defined work: what they will do, what you will pay, what they may tell other clients, who owns what they produce, and how either side ends it.",
    whyItMatters:
      "CPG consultants are valuable precisely because they work across many brands, and that is also the risk. The person helping you land a chain account this quarter may be helping two competitors do the same next quarter, using the shelf plan, the pricing and the buyer feedback you paid for. Meanwhile the deliverables — a formulation, a channel strategy, a costed supply chain model — are often reused wholesale for the next client unless the agreement says otherwise. Handled well, a consultant agreement buys you the expertise and keeps the specifics; handled badly, you fund a competitor's education.",
    majorTerms: [
      "Scope of engagement and deliverables",
      "Retainer, project fee or day rate and what it covers",
      "Time commitment and availability expectations",
      "Expenses, travel and pre-approval thresholds",
      "Ownership of deliverables and of pre-existing consultant materials",
      "Confidentiality and non-use of your information",
      "Conflicts: competing brands, disclosure and any category exclusivity",
      "Term, renewal and termination on notice",
      "Any equity or success-fee component",
      "Independent contractor status",
      "Subcontracting and named-person requirements",
    ],
    clausesToWatch: [
      {
        clause: "Conflicts and competing engagements",
        why: "Without a disclosure obligation and some category limit, a consultant can advise three brands in your set at once and you will only learn about it from a shelf reset.",
      },
      {
        clause: "Ownership versus licence of deliverables",
        why: "Consultants often retain their frameworks and reuse them, which is reasonable, but the version built with your data, pricing and formulation should not travel with them.",
      },
      {
        clause: "Named consultant versus firm",
        why: "You hired a specific person's judgement; without naming them, the work can be delegated to a junior and you pay the senior rate for it.",
      },
      {
        clause: "Non-use of confidential information",
        why: "A confidentiality clause that only bars disclosure still lets a consultant use what they learned from you in another engagement — non-use is the clause that matters.",
      },
      {
        clause: "Success fees and equity",
        why: "A percentage of a deal the consultant helps land needs a definition of what counts and when it is earned, or you will be paying on business you closed yourself.",
      },
      {
        clause: "Termination and retainer commitments",
        why: "A multi-month minimum retainer with no exit means you keep paying long after it is obvious the engagement is not working.",
      },
    ],
    cpgIssues: [
      "Formulation consultants and food scientists must assign the formula, the specification and the process know-how specifically; a generic IP clause about 'reports and documents' can leave the recipe outside the assignment.",
      "Retail and category consultants carry buyer relationships and shelf data across brands — agree what they may reuse, and treat your pricing, promotional calendar and buyer feedback as non-use information.",
      "A consultant working for three competing brands in one category is common and not by itself a problem; the problem is undisclosed overlap, so require disclosure of current and new clients in your category.",
      "Supply chain consultants see your co-packer terms, ingredient pricing and freight rates, which is exactly what a competitor would most like to have.",
      "Regulatory and labelling consultants advise on claims and label copy that you carry the responsibility for; their work should be documented and their scope of advice clearly written.",
      "Consultants who negotiate with your co-packer or distributor should have their authority limited in writing, because a verbal commitment made on your behalf can bind you commercially.",
    ],
    founderMistakes: [
      "Hiring a category consultant on a handshake retainer and later finding the same deck, with the brand name changed, in a competitor's hands.",
      "Paying a formulation consultant for months without a formula assignment, then being unable to move the product to another manufacturer cleanly.",
      "Agreeing a success fee on 'accounts opened' with no definition, then paying commission on a retailer the founder had been working for a year.",
      "Signing a six-month minimum retainer with no termination right for an engagement that stopped producing value in week three.",
      "Assuming the named expert would do the work, and receiving deliverables from an analyst they had never met.",
      "Letting a consultant contact the co-packer directly with no limits, and having a pricing concession offered that the founder never approved.",
    ],
    negotiationPoints: [
      "Require disclosure of current and prospective clients in your category, and agree a narrow, time-limited category restriction with counsel's input.",
      "Assign the deliverables to you, with a licence back for the consultant's genuinely pre-existing frameworks rather than the reverse.",
      "Add non-use as well as non-disclosure, naming pricing, formulations, supplier terms and buyer feedback.",
      "Name the individual who will do the work and require consent before delegation.",
      "Define any success fee precisely: what counts, when it is earned, what happens after termination.",
      "Keep the retainer terminable on short notice after an initial period, with fees payable for work actually done.",
      "Limit their authority to commit the company to anything without written approval.",
    ],
    redFlags: [
      "No conflicts or disclosure obligation in a category where the consultant obviously has other clients.",
      "Consultant retains ownership of everything produced, with you receiving only a licence.",
      "Confidentiality with no non-use obligation.",
      "A success fee with no definition of a qualifying deal.",
      "A long minimum retainer with no termination right.",
      "Broad authority to negotiate with your suppliers, co-packers or customers.",
    ],
    beforeYouSign: [
      "A written scope with deliverables, cadence and what the retainer actually buys in hours or output.",
      "A disclosed list of their current clients in your category.",
      "Agreement on what happens to the deliverables and the data behind them at the end of the engagement.",
    ],
    related: [
      "independent-contractor-agreement",
      "advisor-agreement",
      "contractor-nda",
      "product-development-consultant-agreement",
      "contractor-work-for-hire-agreement",
      "trade-secret-protection-agreement",
    ],
    attorneyReview: "yes",
    templateId: "consultant-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "advisor-agreement",
    name: "Advisor Agreement",
    category: "people",
    subcategory: "Advisors",
    aliases: [
      "Advisory Agreement",
      "Startup Advisor Agreement",
      "Board Advisor Agreement",
      "Strategic Advisor Agreement",
    ],
    stages: ["idea", "pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Advisor",
    whenYouNeedIt:
      "When an experienced operator, buyer, founder or investor offers to help and you plan to pay them in equity, a small fee, or the promise of both. Paper it before the introductions start, not after they work.",
    whatItDoes:
      "Sets what the advisor will actually do, what they receive, over what period it vests, what they may not do with what they learn, and how either side ends the arrangement without drama.",
    whyItMatters:
      "Advisor equity is the cheapest thing in the world to promise and one of the more awkward to unwind. A CPG founder can easily hand out several small grants in a year to people who made one introduction, and the cap table carries all of it into the first priced round. The other exposure is conflict: advisors in this industry are frequently close to a distributor, a retailer, a broker or a competing brand, and an advisor with an undisclosed interest on the other side of a negotiation is a real problem. Both are solved by writing down what they will do, what it is worth, and how the equity behaves if they stop doing it.",
    majorTerms: [
      "Specific expectations: hours, calls, introductions, availability",
      "Term and how it ends",
      "Compensation: equity, cash, or both",
      "Vesting schedule and treatment on termination",
      "The plan and grant documents behind any equity",
      "Confidentiality obligations",
      "Conflicts and disclosure of competing interests",
      "No authority to bind the company",
      "Expenses and travel",
      "Use of the advisor's name and likeness in company materials",
    ],
    clausesToWatch: [
      {
        clause: "Vesting and termination of unvested equity",
        why: "Equity that vests fully on signature, or continues vesting after the advisor stops engaging, pays the same for one introduction as for two years of work.",
      },
      {
        clause: "What the advisor actually commits to",
        why: "An agreement with no stated expectation gives you nothing to point at when the calls stop, and no clean basis to end it.",
      },
      {
        clause: "Conflicts and disclosure",
        why: "Advisors in CPG often sit close to distributors, brokers, retailers and other brands, and an undisclosed interest can distort advice on your most important decisions.",
      },
      {
        clause: "Use of name and likeness",
        why: "Listing an advisor on your site or deck without a written right to do so can force an awkward removal exactly when the logo is doing the most work.",
      },
      {
        clause: "No authority to bind",
        why: "An enthusiastic advisor talking to a retailer or a co-packer can create expectations the company has to honour or walk back.",
      },
      {
        clause: "Confidentiality with a personal-investment carve-out",
        why: "Advisors who also invest may seek information rights, and mixing an advisory role with investor information rights should be a deliberate decision, not a drafting accident.",
      },
    ],
    cpgIssues: [
      "Retail buyer introductions are the most valuable thing a CPG advisor offers and the most likely to be double-counted — if a broker is also paid on the same account, decide in advance who is credited.",
      "An advisor who is also a distributor principal, a broker owner or an executive at a competing brand needs that disclosed in writing before they see your pricing or your P&L.",
      "Advisors frequently see co-packer pricing, margin structure and retailer terms; confidentiality should name those specifically.",
      "Advisors who lend their face to the brand — a known founder, chef or athlete — are appearing in marketing, so likeness rights and a term limit belong in the agreement or a separate release.",
      "Equity grants to advisors sit in the same pool as employee options; issue them through the plan so the cap table stays clean for a raise or a sale.",
      "An advisor bringing a formulation idea or a product concept should be covered by an assignment clause, or the origin of a product feature becomes an argument later.",
    ],
    founderMistakes: [
      "Granting advisor equity that vested immediately in exchange for a promise of introductions that never came.",
      "Issuing advisor shares outside the equity plan, and cleaning up an untidy cap table during a financing.",
      "Never asking an advisor to disclose their other roles, and taking distribution advice from someone with an interest in the distributor.",
      "Listing advisors on the website with no written permission, then having to remove them mid-fundraise.",
      "Letting an advisor negotiate informally with a retail buyer, and inheriting a price expectation the brand could not support.",
      "Continuing to vest equity for two years after the advisor stopped responding, because nothing in the document allowed it to stop.",
    ],
    negotiationPoints: [
      "Write down the expected engagement — hours per month, specific introductions, availability — and tie continued vesting to it.",
      "Use a short vesting schedule with a cliff and immediate cessation on termination.",
      "Issue any equity through the adopted plan with a board-approved grant.",
      "Require written disclosure of roles with distributors, brokers, retailers and competing brands, on signing and on change.",
      "Get an express right to use their name, title and photo, and agree what happens to it when the term ends.",
      "State clearly that the advisor has no authority to bind the company or negotiate on its behalf.",
    ],
    redFlags: [
      "Fully vested equity granted on signature.",
      "No stated expectation of what the advisor will do.",
      "No conflicts disclosure in an industry where advisors routinely have several.",
      "Equity issued outside the plan or without board approval.",
      "Continued vesting after the advisory relationship has ended.",
      "Advisory equity bundled with investor information or consent rights that were never negotiated.",
    ],
    beforeYouSign: [
      "A written list of what you actually want from this advisor over the next year.",
      "Board approval and available pool for the grant.",
      "Their disclosed roles with any distributor, broker, retailer or competing brand.",
    ],
    related: [
      "consultant-agreement",
      "advisory-board-agreement",
      "advisor-equity-agreement",
      "advisory-equity-agreement",
      "equity-incentive-plan",
      "employee-confidentiality-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "employee-confidentiality-agreement",
    name: "Employee Confidentiality Agreement",
    category: "people",
    subcategory: "Employee protections",
    aliases: [
      "Employee NDA",
      "Confidentiality and Non-Disclosure Agreement (Employee)",
      "Employee Confidentiality and Non-Solicitation Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Employee",
    whenYouNeedIt:
      "On or before day one for every employee, and again when someone moves into a role that gives them access to formulations, supplier pricing or customer data they did not have before.",
    whatItDoes:
      "Defines what the company treats as confidential, obliges the employee not to disclose or use it outside their job, and sets out what must be returned or deleted when they leave. Many versions also carry non-solicitation or other restrictive covenants.",
    whyItMatters:
      "In CPG the confidential information that matters is unusually concrete: the formula and its iterations, the co-packer's tolling price, ingredient costs, the margin structure by retailer, the promotional calendar and the list of buyers who actually take your calls. All of it is portable, all of it is valuable to a competitor, and most of it walks out on a phone. This agreement is the difference between having a claim and having a feeling. Where it also contains restrictions on competing or soliciting, note that what those may cover varies significantly by jurisdiction and is a question for counsel where the employee works — an overreaching restriction can be worth less than a carefully scoped one.",
    majorTerms: [
      "Definition of confidential information, with CPG-specific categories named",
      "Non-disclosure and non-use obligations",
      "Duration of the obligations after employment ends",
      "Return and deletion of materials, devices and credentials",
      "Ownership of company accounts, contacts and content",
      "Non-solicitation of employees, if included",
      "Non-solicitation of customers or accounts, if included",
      "Any non-compete, its scope and duration, subject to local review",
      "Carve-outs for information that is genuinely public",
      "Remedies and notice obligations on suspected breach",
    ],
    clausesToWatch: [
      {
        clause: "Definition of confidential information",
        why: "A definition written for a software company will list source code and customer databases and miss the formula, the specification, the tolling price and the promotional calendar.",
      },
      {
        clause: "Non-use as distinct from non-disclosure",
        why: "An employee who never tells anyone your ingredient pricing but uses it to build a competing brand has not breached a disclosure-only clause.",
      },
      {
        clause: "Restrictive covenants",
        why: "How far a restriction on competing or soliciting may go varies sharply by jurisdiction and should be scoped by counsel where the employee works rather than copied from a template.",
      },
      {
        clause: "Return and deletion of materials",
        why: "Without an explicit obligation covering personal devices, cloud accounts and messaging apps, the sales deck and buyer list simply leave with the phone.",
      },
      {
        clause: "Ownership of accounts and contacts",
        why: "Brand social accounts, retailer portals and shared inboxes are often created under a personal login, and ownership needs to be stated before there is a dispute about it.",
      },
      {
        clause: "Duration of obligations",
        why: "Confidentiality that expires on a fixed date can end while the formula it protects is still the thing your business sells.",
      },
    ],
    cpgIssues: [
      "Name the formula, the specification, the ingredient deck, the process parameters and the shelf-life data explicitly, because a generic definition may not obviously reach a recipe.",
      "Co-packer tolling rates, ingredient pricing and freight rates are the numbers a competitor most wants; list supplier and manufacturing economics as their own category.",
      "Retailer and distributor terms — margin, promotional funding, deductions, planogram position — should be named, since a departing sales hire carries them in their head.",
      "Buyer contact lists and the notes behind them are built at your expense and are the most common thing to leave with a salesperson.",
      "Trade-secret protection generally depends on treating the information as secret in practice, so pair this agreement with real access controls rather than relying on the document alone.",
      "Employees who work at a co-packer's site or a shared facility may see other brands' information as well; the obligation should run in both directions to protect the relationship.",
    ],
    founderMistakes: [
      "Using a generic employee NDA that never mentioned formulations, and having no clear claim when an operations hire launched a near-identical product.",
      "Relying on a non-disclosure clause with no non-use obligation, and watching a former employee build a competing brand on knowledge rather than documents.",
      "Copying a restrictive covenant from another jurisdiction's template and never having it reviewed where the employee actually worked.",
      "Never collecting devices, credentials or files on departure, and losing the buyer list and the content library at the same time.",
      "Letting the brand's social accounts remain under a marketing hire's personal login until the day they resigned.",
      "Marking nothing as confidential internally, so the information the agreement protected was not treated as secret in practice.",
    ],
    negotiationPoints: [
      "Write a CPG-specific definition naming formulations, specifications, supplier and co-packer economics, retailer terms and customer lists.",
      "Include non-use alongside non-disclosure.",
      "Add an explicit return-and-delete obligation covering personal devices, cloud storage and messaging apps.",
      "State that all brand accounts, handles, portals and content belong to the company, with credentials handed over on exit.",
      "Have counsel scope any non-compete or non-solicit for the jurisdiction where the employee works.",
      "Keep trade-secret protection open-ended rather than tied to a fixed expiry.",
    ],
    redFlags: [
      "A definition of confidential information that never mentions the product, the formula or the supply chain.",
      "Non-disclosure with no non-use.",
      "Restrictive covenants copied across jurisdictions without review.",
      "No obligation to return or delete material held on personal devices.",
      "Silence on ownership of brand social accounts and retailer portal logins.",
      "A blanket expiry that ends protection of the formula while it is still in market.",
    ],
    beforeYouSign: [
      "A written list of what your company actually treats as confidential, checked against the definition.",
      "Counsel review of any restrictive covenant for the jurisdictions where you employ people.",
      "An offboarding checklist covering devices, credentials, accounts and physical samples.",
    ],
    related: [
      "employee-ip-assignment-agreement",
      "employment-agreement",
      "employee-nda",
      "trade-secret-protection-agreement",
      "formula-ownership-agreement",
      "separation-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Restrictive covenants and post-employment obligations vary sharply by jurisdiction, so the scope that works in one location can be the wrong scope in another.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "employee-ip-assignment-agreement",
    name: "Employee IP Assignment Agreement",
    category: "people",
    subcategory: "Employee protections",
    aliases: [
      "Invention Assignment Agreement",
      "Proprietary Information and Inventions Agreement (Employee)",
      "Employee IP Agreement",
      "Assignment of Inventions",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Employee",
    whenYouNeedIt:
      "On or before an employee's first day, alongside the confidentiality agreement. Also when an existing employee starts working on formulation, packaging, content or anything else the company will own.",
    whatItDoes:
      "Assigns to the company what the employee creates in the course of their work — formulations, artwork, copy, photography, software, process improvements — and obliges them to disclose it, document it and sign whatever is needed to perfect the company's ownership later.",
    whyItMatters:
      "Employment alone does not always put ownership of everything an employee creates in the company's hands, and what an assignment can cover varies by jurisdiction. For a CPG brand the gap shows up in the most valuable places: the flavour variant an operations lead developed on a Saturday, the label illustration a marketing hire drew, the photography sitting in a personal cloud account. Buyers ask for the chain of assignment during diligence, and a missing signature on a person who has since left is one of the harder problems to fix — you have to find them and ask nicely.",
    majorTerms: [
      "Present assignment of inventions and works created in the scope of employment",
      "Disclosure obligation for inventions during employment",
      "Assistance with registrations and further assurances after departure",
      "Prior inventions disclosure and any excluded personal work",
      "Ownership of content, photography and design assets",
      "Ownership of brand accounts, handles and domains created by the employee",
      "Waiver of any applicable moral rights, where permitted",
      "Return of source files, working files and originals",
      "Interaction with the confidentiality agreement",
      "Any jurisdiction-specific notice or carve-out required",
    ],
    clausesToWatch: [
      {
        clause: "Present assignment versus promise to assign",
        why: "A promise to assign in the future can leave a gap if the person leaves before signing anything else, while a present assignment operates on creation.",
      },
      {
        clause: "Scope of assignment",
        why: "Language limited to 'inventions' may not clearly cover recipes, artwork, photography and copy, which is most of what a CPG employee actually creates.",
      },
      {
        clause: "Further assurances after departure",
        why: "Without an obligation to sign what is needed later, perfecting a trademark or copyright record can depend on the goodwill of someone who left badly.",
      },
      {
        clause: "Prior inventions schedule",
        why: "If the employee arrives with an existing recipe or brand of their own and never lists it, the boundary between their work and yours is undefined.",
      },
      {
        clause: "Jurisdiction-specific limits and notices",
        why: "What an employee invention assignment may cover, and what notice must accompany it, varies by location and is a point to confirm with counsel.",
      },
      {
        clause: "Source and working files",
        why: "Owning the final artwork but not the layered working file leaves you unable to make a label revision without going back to the person who left.",
      },
    ],
    cpgIssues: [
      "Formulations, flavour variants and process improvements are the highest-value employee output in CPG and are not obviously 'inventions' in everyday language — name them.",
      "Packaging artwork, label copy, product photography and video are created constantly by marketing employees and end up scattered across personal drives and phones.",
      "Brand social handles, e-commerce store logins, retailer portals and email lists are frequently opened in an employee's own name; assign them and require credential handover.",
      "Recipes shown on your channels, developed by an employee at home, are the kind of grey-area asset a buyer will ask about during diligence.",
      "An employee who arrives from a competitor may bring material they should not; a prior-inventions disclosure plus a written confirmation that they bring nothing confidential protects both sides.",
      "Trademark filings, artwork registrations and domain records sometimes end up in an employee's personal name, so the assignment should cover registrations and account ownership explicitly.",
    ],
    founderMistakes: [
      "Never signing invention assignment with the employee who developed the second flavour, and having to track them down years later during diligence.",
      "Owning the exported label artwork but not the working files, and paying to have the whole label rebuilt for a compliance change.",
      "Letting the brand's photography live in a marketing employee's personal cloud account until they left and access was lost.",
      "Discovering a domain and a social handle registered in a former employee's personal name.",
      "Hiring from a competitor with no prior-inventions disclosure and no confirmation about materials brought along.",
      "Assuming employment automatically transferred ownership of everything, without checking what applies where the employee works.",
    ],
    negotiationPoints: [
      "Use a present assignment that operates on creation, not a promise to assign later.",
      "Name formulations, specifications, artwork, copy, photography, video and process improvements in the scope.",
      "Include further assurances and a limited power to complete filings if the person is unreachable, subject to counsel's advice.",
      "Require a prior-inventions schedule and a written confirmation that no former employer's material is being brought in.",
      "Assign accounts, handles, domains and registrations explicitly, with credential handover on exit.",
      "Require delivery of source and working files, not just finished exports.",
    ],
    redFlags: [
      "A promise to assign in the future with no present assignment.",
      "A scope that covers inventions but never mentions creative or recipe work.",
      "No further-assurances obligation after employment ends.",
      "No prior-inventions disclosure from a hire coming out of the same category.",
      "Silence on ownership of accounts, handles and domains.",
      "No obligation to hand over working files.",
    ],
    beforeYouSign: [
      "A list of every account, domain, handle and registration the role will touch.",
      "The employee's prior-inventions disclosure, completed rather than left blank.",
      "Counsel confirmation that the assignment language fits the jurisdiction where the employee works.",
    ],
    related: [
      "employee-confidentiality-agreement",
      "piia",
      "ip-assignment-agreement",
      "contractor-work-for-hire-agreement",
      "employment-agreement",
      "packaging-artwork-ownership",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "contractor-work-for-hire-agreement",
    name: "Contractor Work-for-Hire Agreement",
    category: "people",
    subcategory: "Contractors",
    aliases: [
      "Work Made for Hire Agreement",
      "Creative Services Agreement",
      "Design Work-for-Hire Agreement",
      "Freelance Work-for-Hire Agreement",
    ],
    stages: ["idea", "development", "pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Creative Contractor",
    whenYouNeedIt:
      "Before a freelancer starts anything creative: the logo, the label, the can artwork, the photography, the video, the website, the packaging dielines. Before, not after the invoice.",
    whatItDoes:
      "Commissions creative work and moves ownership of it to the company — by treating it as work made for hire where that applies, and by adding a full present assignment as a backstop so ownership transfers either way.",
    whyItMatters:
      "This is the most common expensive gap in a young CPG brand. The designer who made your logo and your label owns that work until they assign it in writing; a brief, an invoice and a thank-you are not an assignment. The consequences arrive later and all at once: you cannot register the artwork, you cannot license the brand, you cannot make a compliance change to the label without the original files, and a buyer's diligence list asks for the assignment you never got. Rebuying rights from a freelancer who now knows exactly how much the brand is worth is not a negotiation you want to have.",
    majorTerms: [
      "Description of the work and the deliverables",
      "Work-made-for-hire designation plus a present assignment backstop",
      "Delivery of source, layered and working files",
      "Fonts, stock imagery, music and other third-party licences",
      "Rights granted for packaging, advertising, resale and sublicensing",
      "Moral rights waiver where permitted, and credit expectations",
      "Portfolio use and any restriction before launch",
      "Revision rounds included and the rate beyond them",
      "Payment schedule and whether assignment is conditioned on payment",
      "Confidentiality before launch",
      "Warranty of originality and non-infringement",
    ],
    clausesToWatch: [
      {
        clause: "Work-for-hire language alone",
        why: "The work-for-hire concept does not apply to every kind of commissioned work in every place, so an agreement relying on it without a present assignment backstop can leave ownership unresolved.",
      },
      {
        clause: "Assignment conditioned on final payment",
        why: "Reasonable in principle, but if a payment dispute freezes ownership you can be unable to print a label you have already scheduled a production run around.",
      },
      {
        clause: "Third-party fonts, stock and music",
        why: "A licence held by the designer, or one that covers comps but not commercial packaging, can make your finished can unusable at scale.",
      },
      {
        clause: "Source and working files",
        why: "Without an express delivery obligation you get flattened exports and have to pay someone to rebuild the file for every future label revision.",
      },
      {
        clause: "Scope of rights granted",
        why: "Rights limited to 'the campaign' or 'digital use' will not cover packaging, paid advertising, retail displays or a licensing deal later.",
      },
      {
        clause: "Portfolio and publicity use",
        why: "A designer posting your packaging before launch can expose a new product to competitors and retailers earlier than you intended.",
      },
      {
        clause: "Originality warranty",
        why: "Without a warranty and an indemnity, an artwork similarity problem discovered after a production run is entirely your cost.",
      },
    ],
    cpgIssues: [
      "Packaging artwork is the asset most often left unassigned in CPG, and it is also the one you must be able to modify quickly for an ingredient change, a net-content change or a claim revision.",
      "Dielines, print-ready files and colour separations belong with the printer-ready package; without them a can or label change means starting the artwork again.",
      "Fonts licensed for desktop use are not always licensed for packaging or for embedding in print files at production volumes — confirm the licence covers commercial packaging.",
      "Stock imagery licences frequently exclude use on product packaging or on items for resale, which is exactly what you intend to do with it.",
      "Product photography shot by a contractor is reused for years across retailer portals, e-commerce listings and paid ads, so the grant needs to be perpetual and sublicensable rather than campaign-limited.",
      "Illustrations and characters used on packaging can become the brand's most valuable asset; ownership of them should sit with the company, not licensed from the illustrator.",
      "If the contractor's work will appear in retailer new-item submissions, you need the right to sublicense it to the retailer without asking again.",
    ],
    founderMistakes: [
      "Paying a freelancer for the logo and the label, never getting an assignment, and being asked for one during acquisition diligence years later.",
      "Receiving flattened files only, and paying to rebuild the entire label artwork for a small ingredient change.",
      "Launching with a stock image licensed for web use on the front of the packaging, and having to reprint.",
      "Commissioning packaging under an agreement that granted rights for 'marketing use', then finding no clear right to use the artwork on the product itself.",
      "Having a designer post the unreleased can design to a portfolio site weeks before the retailer reset.",
      "Buying rights back from a freelancer after the brand had grown, at a price set by how well the brand was doing.",
    ],
    negotiationPoints: [
      "Use work-for-hire language and a present assignment together, so ownership transfers regardless of which applies.",
      "Require delivery of source, layered and print-ready files as a condition of final payment.",
      "Require disclosure and pass-through of every third-party licence, with rights covering packaging, resale and paid advertising.",
      "Take rights that are perpetual, worldwide, transferable and sublicensable, so a retailer or an acquirer can use them.",
      "Include an originality warranty and a proportionate indemnity.",
      "Restrict portfolio use until an agreed launch date.",
      "Keep a limited licence to use deliverables in the interim if final payment is disputed, so a production run is never blocked.",
    ],
    redFlags: [
      "Work-for-hire wording with no assignment backstop.",
      "A licence to the company instead of ownership.",
      "No obligation to deliver source or print-ready files.",
      "Silence on fonts, stock imagery and third-party material.",
      "Rights limited by medium, term or territory for something going on packaging.",
      "No originality warranty for artwork going to print at volume.",
      "Unrestricted portfolio publication before launch.",
    ],
    beforeYouSign: [
      "A written deliverables list including source files, dielines and print-ready formats.",
      "The list of fonts, stock and third-party assets to be used, with the licence terms for each.",
      "Confirmation that rights are perpetual, transferable and sublicensable.",
    ],
    related: [
      "independent-contractor-agreement",
      "work-for-hire-agreement",
      "packaging-artwork-ownership",
      "design-ownership-agreement",
      "packaging-design-agreement",
      "copyright-assignment",
    ],
    attorneyReview: "yes",
    templateId: "contractor-work-for-hire-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "commission-agreement",
    name: "Commission Agreement",
    category: "people",
    subcategory: "Sales compensation",
    aliases: [
      "Sales Commission Agreement",
      "Commission Plan",
      "Incentive Compensation Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Salesperson or Sales Representative",
    whenYouNeedIt:
      "Whenever someone is paid on what they sell — an employee, a contractor rep, a part-time account manager. Put it in writing before the first order, because commission disputes are decided by what the plan said at the time.",
    whatItDoes:
      "Sets the commission rate, the base it is calculated on, the moment commission is earned, when it is paid, what reduces it, and what happens to orders and accounts after the relationship ends.",
    whyItMatters:
      "The commission tail is where this goes wrong. A salesperson opens a chain in March, leaves in June, and the first meaningful purchase orders ship in September — who gets paid, on what, and for how long? A plan that never answers that produces the most common and most bitter dispute in CPG sales. The second issue is the base: commission on gross invoice value in a category where distributors and retailers deduct promotional funding, spoils and slotting can pay out more than the order eventually earns, so the base has to reflect what actually clears.",
    majorTerms: [
      "Commission rate and any tiers or accelerators",
      "The base: gross invoice, net of deductions, collected revenue or margin",
      "When commission is earned: order, shipment, invoice or payment",
      "Payment timing and frequency",
      "House accounts and excluded accounts",
      "Split credit rules between people and channels",
      "Chargebacks for returns, deductions and unpaid invoices",
      "Draw against commission and whether it is recoverable",
      "Quota, targets and how they are reset",
      "Post-termination commission tail and its duration",
      "Treatment of accounts and orders in flight at termination",
    ],
    clausesToWatch: [
      {
        clause: "When commission is earned",
        why: "Earned on order, on shipment, on invoice or on payment are four different plans, and the difference decides who carries the risk of a cancelled order or an unpaid invoice.",
      },
      {
        clause: "Post-termination tail",
        why: "Silence here means the departing salesperson argues for everything and the company argues for nothing, and neither position is written down anywhere.",
      },
      {
        clause: "The commission base",
        why: "Commission on gross invoice in a channel full of deductions, spoils and promotional funding can exceed the margin the order actually produced.",
      },
      {
        clause: "House accounts",
        why: "If accounts you already had are not excluded by name, you can pay commission on business the salesperson never touched.",
      },
      {
        clause: "Chargebacks and clawbacks",
        why: "Recovering commission already paid on a returned or deducted order needs an express mechanism, and the rules for making deductions from pay vary by jurisdiction and by worker type.",
      },
      {
        clause: "Recoverable versus non-recoverable draw",
        why: "A draw that is quietly recoverable turns a guarantee into a debt the salesperson does not realise they are accumulating.",
      },
      {
        clause: "Plan amendment rights",
        why: "A right to change the plan at any time, applied to orders already in the pipeline, undermines the incentive and invites a claim.",
      },
    ],
    cpgIssues: [
      "The gap between an account opening and the first meaningful purchase order can be many months in retail, so the earning trigger and the tail matter far more here than in faster-cycle industries.",
      "Deductions, promotional allowances, slotting, spoils and free fills all reduce what you actually collect on an order — decide whether commission is calculated before or after them and say so.",
      "Distributor purchase orders are not sell-through; a plan paying on distributor shipments can pay handsomely for inventory that later comes back as unsaleable.",
      "Broker commissions and employee commissions can overlap on the same account; define who is credited when a broker and an in-house rep both touch a chain.",
      "Chain authorizations are often won by one person and executed by another over a long timeline, so split-credit rules need to exist before the win.",
      "Sampling, demo and promotional volume should usually be excluded from the commissionable base, since it is designed to break even at best.",
      "Retail accounts that pay slowly or deduct aggressively make collected-revenue based plans much fairer to the company, but they also delay the salesperson's pay, so set expectations up front.",
    ],
    founderMistakes: [
      "Never writing down what happens to commission after departure, then paying a former salesperson for a year on accounts someone else was servicing.",
      "Paying commission on gross invoice in a deduction-heavy channel and paying out more than the account contributed.",
      "Failing to name house accounts, and paying commission on the founder's own long-standing customers.",
      "Paying on distributor shipments and then eating both the unsaleable credit and the commission when the product came back.",
      "Changing the plan mid-year and applying it to a pipeline built under the old one.",
      "Running a draw with no written statement of whether it was recoverable, and having no basis to recover it.",
    ],
    negotiationPoints: [
      "State the earning trigger explicitly, and prefer collected revenue in channels with heavy deductions.",
      "Define the commission base net of deductions, allowances, freight and returns, with an example calculation attached.",
      "Write the post-termination tail: which orders, which accounts, for how long, and on what condition.",
      "List house and excluded accounts by name as a schedule that is updated and signed.",
      "Set out chargeback rules for returns and deductions, and confirm with counsel how they may be applied to the worker type involved.",
      "State clearly whether a draw is recoverable, and cap the recovery.",
      "Limit plan amendments to future periods with notice, protecting orders already in the pipeline.",
    ],
    redFlags: [
      "No stated moment when commission is earned.",
      "No post-termination provision of any kind.",
      "Commission on gross invoice value with no adjustment for deductions or returns.",
      "No house account schedule.",
      "An unlimited right to amend the plan retroactively.",
      "A draw with no written statement of recoverability.",
      "Chargeback rights drafted without regard to the rules that apply where the person works.",
    ],
    beforeYouSign: [
      "A worked example of a real order run through the plan, from purchase order to net collected.",
      "The named house account schedule.",
      "Counsel input on chargeback and deduction mechanics for the jurisdiction and worker type.",
    ],
    related: [
      "sales-employee-compensation-agreement",
      "commission-sales-agreement",
      "independent-sales-rep-agreement",
      "cpg-broker-agreement",
      "employment-agreement",
      "independent-contractor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sales-employee-compensation-agreement",
    name: "Sales Employee Compensation Agreement",
    category: "people",
    subcategory: "Sales compensation",
    aliases: [
      "Sales Compensation Plan",
      "Sales Incentive Plan",
      "Account Manager Compensation Agreement",
      "Variable Compensation Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Sales Employee",
    whenYouNeedIt:
      "When you put a salesperson on payroll with a variable component — a national account manager, a regional sales lead, a field sales team — and the plan needs to be issued and signed each period.",
    whatItDoes:
      "Documents the whole compensation package for an employed salesperson: base salary, quota, the variable formula, the timing of payments, and the rules that govern adjustments, disputes and departures.",
    whyItMatters:
      "Employed salespeople bring a set of rules that contractors do not: when variable pay must be paid, what may be deducted from it, and what happens on termination are all governed by requirements that vary by jurisdiction and are not negotiable by agreement. Meanwhile the commercial design has to survive the reality of CPG — a plan built on distributor shipments rewards loading a warehouse, and a plan built on gross revenue ignores the deductions that decide whether the account was worth having. Get both halves right, in writing, and reissue the plan whenever it changes.",
    majorTerms: [
      "Base salary and pay frequency",
      "Quota, targets and the period they are measured over",
      "Variable formula: commission, bonus, MBO or a blend",
      "The metric: shipments, depletions, net revenue, margin or points of distribution",
      "Payment timing and frequency of variable pay",
      "Draw arrangements and recoverability",
      "Adjustments for returns, deductions and unpaid invoices",
      "Account assignment, territory and house accounts",
      "Treatment on termination, including in-flight orders",
      "Plan period, reissue and amendment process",
      "Expense policy for travel, samples and trade shows",
    ],
    clausesToWatch: [
      {
        clause: "Payment timing for variable pay",
        why: "When earned variable compensation must be paid to an employee is set by rules that differ by jurisdiction, so a plan designed purely around cash flow can conflict with them.",
      },
      {
        clause: "Deductions and clawbacks from pay",
        why: "What can be recovered from an employee's compensation, and how, is a jurisdiction-specific question that a commercially sensible clawback clause can easily get wrong.",
      },
      {
        clause: "The performance metric",
        why: "Paying on distributor shipments encourages warehouse loading, while paying on depletions or collected revenue tracks what the business actually earned.",
      },
      {
        clause: "Quota reset and mid-year changes",
        why: "Raising quota mid-period, or reassigning accounts after a win, is the fastest way to lose a good salesperson and to acquire a claim.",
      },
      {
        clause: "Account reassignment",
        why: "Moving a major account to another rep after the work was done, with no transition credit, converts a compensation plan into a dispute.",
      },
      {
        clause: "Termination treatment",
        why: "A plan stating that no variable pay is due unless the employee is employed on the payment date can conflict with local requirements and should be reviewed.",
      },
    ],
    cpgIssues: [
      "Distributor shipments are the easiest metric to measure and the worst to pay on, because a warehouse load looks identical to real demand until the product comes back.",
      "Depletion and sell-through data is the better metric but arrives late and unevenly from distributors, so the plan needs to say what happens when data is missing or restated.",
      "Retail deductions, slotting, promotional funding and spoils can turn a headline order into a marginal one; the plan should reflect net contribution rather than invoice value.",
      "Points of distribution and new-item authorizations are often the real objective in a launch year, and a plan that pays only on revenue will not produce them.",
      "Trade spend is usually controlled by the same salesperson earning on the revenue it generates, so the plan should account for spend against the account rather than ignoring it.",
      "Sales roles in CPG carry heavy sample, travel and trade-show expense; an expense policy belongs alongside the plan rather than in a separate conversation.",
    ],
    founderMistakes: [
      "Paying on distributor purchase orders in year one, funding a warehouse load, and paying full commission on inventory that came back as unsaleable.",
      "Raising quota mid-year after an unusually good quarter, and losing the salesperson and the accounts at the same time.",
      "Writing a clawback for deductions into an employee plan without checking how deductions from employee pay are treated where the person works.",
      "Never reissuing the plan, so a document written for a launch year governed a business three times the size.",
      "Reassigning a chain account to a new regional lead immediately after it was won, with no transition credit and no warning.",
      "Ignoring trade spend in the plan, so the most incentivised behaviour was to buy volume with promotional funding.",
    ],
    negotiationPoints: [
      "Choose a metric that matches the stage: authorizations and points of distribution early, depletions and net revenue once the base exists.",
      "Attach a worked example showing a real order from purchase order to net contribution and the resulting payout.",
      "Set the payment timing with counsel's input for the jurisdiction where the employee works.",
      "State the plan period and reissue it in writing each period rather than amending mid-flight.",
      "Define account assignment, transitions and split credit before the season starts.",
      "Account for trade spend against the account, so revenue bought with promotional funding is not rewarded as if it were organic.",
      "Have counsel review clawback, deduction and termination language before the plan is issued.",
    ],
    redFlags: [
      "Variable pay conditioned on employment at the payment date with no review of local requirements.",
      "Deductions from pay drafted purely commercially.",
      "A metric based only on distributor shipments.",
      "A right to change quota or reassign accounts at any time with no notice.",
      "No stated plan period and no reissue process.",
      "No expense policy for a role that will spend heavily on travel and samples.",
    ],
    beforeYouSign: [
      "A worked payout example on real historic orders, including deductions and returns.",
      "Counsel review of payment timing, deductions and termination treatment where the employee works.",
      "The account assignment schedule and the house account list.",
    ],
    related: [
      "commission-agreement",
      "employment-agreement",
      "offer-letter",
      "key-account-sales-agreement",
      "cpg-broker-agreement",
      "separation-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Payment timing for earned variable compensation, and what may be deducted or clawed back from an employee's pay, are set by jurisdiction-specific rules rather than by the plan.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "separation-agreement",
    name: "Separation Agreement",
    category: "people",
    subcategory: "Exits",
    aliases: [
      "Severance Agreement",
      "Separation and Release Agreement",
      "Termination and Release Agreement",
      "Exit Agreement",
    ],
    stages: ["growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Departing Employee",
    whenYouNeedIt:
      "When an employment relationship ends and you want certainty rather than an open question — typically where severance is being paid, where the departure is contested, or where the person held equity, accounts or information that matters.",
    whatItDoes:
      "Records the terms of the exit: final pay, any severance, treatment of equity, continuation of confidentiality and IP obligations, return of property, and usually a release of claims in exchange for consideration the employee was not already owed.",
    whyItMatters:
      "This is the document that decides whether a difficult departure is finished or merely paused. For a CPG company the practical stakes are as high as the legal ones: the departing person may hold the retailer relationships, the co-packer contacts, the brand's social logins and a car full of samples, and the separation agreement is the last moment you have real leverage to get all of it back. The legal side — what a release can cover, what notice or review periods apply, how severance interacts with final pay, what may be said about the departure — is jurisdiction-specific and changes; this is a document to have drafted rather than downloaded.",
    majorTerms: [
      "Separation date and reason as recorded",
      "Final pay, accrued time and expense reimbursement",
      "Severance amount, form and payment schedule",
      "Release of claims and its scope",
      "Treatment of vested and unvested equity, and any exercise window",
      "Continuing confidentiality, non-use and IP obligations",
      "Return of property, samples, inventory, devices and credentials",
      "Transfer of accounts, logins, contacts and files",
      "Non-disparagement, if included, and whether it is mutual",
      "Reference and communication handling",
      "Cooperation with transition, audits or ongoing matters",
    ],
    clausesToWatch: [
      {
        clause: "Scope of the release",
        why: "What a release can validly cover, and what has to be excluded or specifically called out, varies by jurisdiction and is the reason this document goes to counsel.",
      },
      {
        clause: "Consideration for the release",
        why: "A release usually needs to be supported by something the employee was not already entitled to, so paying only what was already owed can leave you with nothing in return.",
      },
      {
        clause: "Equity treatment and exercise windows",
        why: "Silence on unvested equity, or an exercise window that passes unnoticed, produces a dispute months later when the shares matter.",
      },
      {
        clause: "Return of property and credentials",
        why: "Without a specific list, the samples, the branded cooler, the retailer portal login and the shared inbox stay wherever they are.",
      },
      {
        clause: "Non-disparagement",
        why: "A one-way clause is worth less than a mutual one, and any restriction on what a departing person may say needs to be scoped by counsel.",
      },
      {
        clause: "Post-employment restrictions restated",
        why: "Restating a non-compete or non-solicit at exit does not make it broader than it could be in the first place, so the underlying scope still needs local review.",
      },
      {
        clause: "Cooperation obligation",
        why: "Without it, you lose access to the person who knows why the co-packer changed the spec or what the buyer actually agreed.",
      },
    ],
    cpgIssues: [
      "Departing sales staff hold buyer relationships, retailer portal credentials and pending purchase orders; the agreement should require a written handover list, not just a promise.",
      "Field and demo staff often hold inventory, samples, coolers, tents and branded assets at home or in a vehicle — inventory them and require return by a date.",
      "Brand social accounts, review platforms, e-commerce admin and retailer vendor portals are frequently tied to a personal login and need explicit transfer.",
      "Operations departures should include a knowledge handover of run parameters, specification versions and open quality issues with the co-packer.",
      "Commission owed on orders shipping after departure needs to be settled here, in a number, rather than left to the underlying plan and a later argument.",
      "A departing employee moving to a competitor in the same category raises confidentiality questions about formulations and supplier pricing that should be restated in writing at exit.",
    ],
    founderMistakes: [
      "Paying severance without a release, and being no better protected than if nothing had been signed.",
      "Using a template found online in a jurisdiction whose requirements it did not meet, and getting neither certainty nor goodwill.",
      "Letting a sales lead leave without transferring retailer portal logins, and losing access to the vendor accounts for weeks.",
      "Never inventorying the samples, coolers and event equipment held by a field team member who left.",
      "Leaving post-departure commission unresolved in the separation agreement and litigating it afterwards.",
      "Agreeing a one-way non-disparagement clause and then being freely criticised in the industry anyway.",
    ],
    negotiationPoints: [
      "Have counsel in the relevant jurisdiction draft or review the release, the consideration and any review or revocation periods.",
      "Settle post-departure commission as a stated figure rather than a formula to be applied later.",
      "Attach an itemised property list — devices, samples, inventory, equipment, credentials — with a return date.",
      "Require a written handover of accounts, contacts, open orders and pending retailer commitments.",
      "State equity treatment explicitly, including exercise windows and any extension.",
      "Make non-disparagement mutual if it is included at all.",
      "Add a reasonable cooperation obligation for transition and any open supplier or retailer matter.",
    ],
    redFlags: [
      "A release supported only by amounts already owed.",
      "A template used across jurisdictions without review.",
      "No property, credential or account handover list.",
      "Silence on equity and exercise windows.",
      "Post-departure commission left to be worked out later.",
      "A one-way non-disparagement clause presented as standard.",
    ],
    beforeYouSign: [
      "Counsel review in the jurisdiction where the employee worked.",
      "A complete inventory of company property, credentials and accounts the person holds.",
      "A final pay and commission calculation you can defend line by line.",
    ],
    related: [
      "employment-agreement",
      "employee-confidentiality-agreement",
      "commission-agreement",
      "general-release",
      "contractor-termination-agreement",
      "mutual-release",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "What a release can cover, what consideration and review periods are required, and how final pay must be handled are jurisdiction-specific, and a defective release leaves you paying severance for nothing.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "contractor-termination-agreement",
    name: "Contractor Termination Agreement",
    category: "people",
    subcategory: "Exits",
    aliases: [
      "Contractor Separation Agreement",
      "Termination of Services Agreement",
      "Engagement Wind-Down Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Departing Contractor or Consultant",
    whenYouNeedIt:
      "When a contractor or consulting engagement ends and something is unresolved: unfinished deliverables, unpaid invoices, files you do not have, an assignment that was never signed, or a disagreement about what was owed.",
    whatItDoes:
      "Closes out the engagement in writing: what is finished, what is being paid, what is being handed over, that ownership of everything created has transferred, and that neither side has further claims arising from the work.",
    whyItMatters:
      "Contractor engagements end informally, which is exactly why the loose ends survive. The two that matter most in CPG are files and ownership: the designer who never delivered the layered artwork, and the developer, photographer or formulator whose assignment was never signed. Both problems are cheap to fix at the end of an engagement, when there is still a final invoice on the table, and expensive to fix two years later when you are answering a buyer's diligence request and the contractor has moved on.",
    majorTerms: [
      "Effective end date of the engagement",
      "Final deliverables and the condition they must be in",
      "Final fee, expenses and payment date",
      "Confirmation or grant of assignment of all work product",
      "Delivery of source, working and print-ready files",
      "Return or deletion of confidential material and samples",
      "Transfer of accounts, credentials, domains and logins",
      "Continuing confidentiality and non-use obligations",
      "Mutual release of claims arising from the engagement",
      "Any transition assistance and its rate",
      "Portfolio use permissions going forward",
    ],
    clausesToWatch: [
      {
        clause: "Confirmation of assignment",
        why: "If the original agreement was thin or missing, this is the moment to get a full present assignment signed, while the final payment is still leverage.",
      },
      {
        clause: "File delivery in usable formats",
        why: "A general obligation to 'return materials' does not produce layered artwork, dielines, raw footage or a code repository unless the formats are listed.",
      },
      {
        clause: "Scope of the release",
        why: "A release that covers only the company's claims leaves the contractor free to come back on fees, while a mutual one closes both directions.",
      },
      {
        clause: "Transition assistance",
        why: "Without an agreed obligation and rate, the questions that arise a month later go unanswered, or get answered at whatever rate is quoted then.",
      },
      {
        clause: "Credential and account handover",
        why: "Contractors frequently hold ad accounts, domain registrations, analytics and store admin access that is very hard to recover once goodwill is gone.",
      },
      {
        clause: "Portfolio and publicity rights",
        why: "Agreeing now what they may show and when avoids an unannounced reveal of work you have not launched.",
      },
    ],
    cpgIssues: [
      "Packaging files are the highest-risk item: dielines, layered artwork and print-ready separations should be listed by name and delivered before the final payment clears.",
      "Product photography and video shot for the brand tends to live in the contractor's own storage; specify delivery of raw and edited files and confirm the licence covers packaging, retailer portals and paid media.",
      "A formulation or product development contractor should confirm assignment of the formula, specification and process notes, and return or destroy any samples and retained material.",
      "Contractors who ran ad accounts, domains or the e-commerce store often hold administrative access that outlives the engagement; transfer it as part of the exit.",
      "Field and demo contractors may hold inventory, samples and event equipment that never appears on a balance sheet until it is missing.",
      "Confidentiality should continue past the engagement for supplier pricing, co-packer terms and unreleased product plans they saw while working.",
    ],
    founderMistakes: [
      "Ending a design engagement on good terms with no file handover, then paying to rebuild artwork for a label change.",
      "Paying a final invoice before securing the assignment that had never been signed at the start.",
      "Losing administrative access to the ad account and domain because the contractor's access was never transferred.",
      "Letting a formulation consultant keep samples and specification documents after the engagement ended.",
      "Accepting a one-way release and receiving a fee claim months later.",
      "Never agreeing portfolio terms, and having unreleased packaging appear publicly before the retailer reset.",
    ],
    negotiationPoints: [
      "Make final payment conditional on delivery of a listed set of files in named formats.",
      "Include a present assignment of all work product, whether or not one was signed at the start.",
      "Make the release mutual and limited to claims arising from the engagement.",
      "List every account, domain, credential and platform to be transferred, with a completion date.",
      "Agree a transition assistance period and an hourly rate for it in advance.",
      "Set portfolio permissions with a start date tied to your launch.",
    ],
    redFlags: [
      "Final payment released before files and assignments are delivered.",
      "No confirmation of ownership where the original agreement was silent.",
      "A one-way release in the contractor's favour.",
      "No list of accounts or credentials to be transferred.",
      "No continuing confidentiality obligation.",
      "Unrestricted portfolio publication of unreleased work.",
    ],
    beforeYouSign: [
      "A checklist of every file, account, credential and physical item the contractor holds.",
      "A reconciled statement of what has been invoiced, paid and remains outstanding.",
      "Confirmation that the assignment language covers everything created during the engagement.",
    ],
    related: [
      "independent-contractor-agreement",
      "contractor-work-for-hire-agreement",
      "consultant-agreement",
      "separation-agreement",
      "mutual-release",
      "mutual-termination-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default PEOPLE_DOCS;
